import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  AppointmentStatusSchema,
  AppointmentTransportTypeSchema,
  AppointmentTypeSchema,
  VinSchema,
  utcToWallTime,
  wallClockDate,
  wallTimeToUtc,
  type Appointment,
  type Rooftop,
  type UpdateAppointmentDto,
} from "@lumika/shared";
import {
  Button,
  ConfirmationDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  humanize,
  toast,
  useZodForm,
} from "@lumika/ui";
import { bdcApi } from "../api/bdc";
import { useRooftopZone } from "../lib/rooftop-zone";
import { assignableUsers } from "../lib/user";
import { ContactSearchPicker } from "./ContactSearchPicker";

type AppointmentStatus = z.infer<typeof AppointmentStatusSchema>;
type AppointmentType = z.infer<typeof AppointmentTypeSchema>;
type TransportType = z.infer<typeof AppointmentTransportTypeSchema>;

const AppointmentFormSchema = z.object({
  contactId: z.string().uuid("Pick a contact."),
  rooftopId: z.string().uuid("Pick a rooftop."),
  appointmentType: AppointmentTypeSchema,
  serviceTypeId: z.string().uuid().or(z.literal("none")),
  status: AppointmentStatusSchema,
  scheduledStartAt: z.string().min(1, "Pick a date and time."),
  durationMinutes: z
    .number({ invalid_type_error: "Duration must be a number." })
    .int()
    .positive()
    .max(24 * 60),
  assignedUserId: z.string().uuid().or(z.literal("none")),
  transportType: AppointmentTransportTypeSchema.or(z.literal("none")),
  vehicleVin: z.union([z.literal(""), VinSchema]).optional(),
  notes: z.string().trim().max(4000).optional(),
});

type AppointmentFormValues = z.infer<typeof AppointmentFormSchema>;

const EMPTY: AppointmentFormValues = {
  contactId: "",
  rooftopId: "",
  appointmentType: "sales",
  serviceTypeId: "none",
  status: "unconfirmed",
  // Filled at dialog open (defaultSlot()) — a module-level default
  // computed once at load would go stale in a long-lived tab.
  scheduledStartAt: "",
  durationMinutes: 30,
  assignedUserId: "none",
  transportType: "none",
  vehicleVin: "",
  notes: "",
};

function defaultSlot(zone: string): string {
  // Next full hour ON THE STORE'S CLOCK, clamped into plausible
  // showroom hours — a dialog opened at night otherwise proposed
  // times like 02:00 (and an operator abroad got their own wall
  // clock, not the store's).
  const OPEN_HOUR = 9;
  const LAST_SLOT_HOUR = 18;
  const date = wallClockDate(new Date(), zone);
  date.setHours(date.getHours() + 1, 0, 0, 0);
  if (date.getHours() < OPEN_HOUR) {
    date.setHours(OPEN_HOUR, 0, 0, 0);
  } else if (date.getHours() > LAST_SLOT_HOUR) {
    date.setDate(date.getDate() + 1);
    date.setHours(OPEN_HOUR, 0, 0, 0);
  }
  return toAppointmentPresetStart(date);
}

/**
 * Date → the `presetStart` string this dialog accepts: the date's own
 * COMPONENTS as store wall time, not an instant. The calendar grid lays
 * events out in store wall time, so a clicked slot's components already
 * are the wall time the operator meant; converting it as an instant would
 * shift the booking by the browser-vs-store offset.
 */
export function toAppointmentPresetStart(value: Date): string {
  const yyyy = value.getFullYear();
  const mm = String(value.getMonth() + 1).padStart(2, "0");
  const dd = String(value.getDate()).padStart(2, "0");
  const hh = String(value.getHours()).padStart(2, "0");
  const mi = String(value.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function toEditValues(appointment: Appointment, zone: string): AppointmentFormValues {
  return {
    contactId: appointment.contactId,
    rooftopId: appointment.rooftopId,
    appointmentType: appointment.appointmentType,
    serviceTypeId: appointment.serviceTypeId ?? "none",
    status: appointment.status,
    // The datetime-local field shows/edits the STORE's wall time.
    scheduledStartAt: utcToWallTime(appointment.scheduledStartAt, zone),
    durationMinutes: appointment.durationMinutes,
    assignedUserId: appointment.assignedUserId ?? "none",
    transportType: appointment.transportType ?? "none",
    vehicleVin: appointment.vehicleVin ?? "",
    notes: appointment.notes ?? "",
  };
}

function toUpdatePayload(values: AppointmentFormValues, zone: string): UpdateAppointmentDto {
  return {
    appointmentType: values.appointmentType,
    serviceTypeId: values.serviceTypeId === "none" ? null : values.serviceTypeId,
    status: values.status,
    // The form value is the store's wall time — convert via the
    // rooftop zone, NOT the browser's (the old `new Date(naive)` parse
    // shifted bookings by the operator-vs-store timezone delta).
    scheduledStartAt: wallTimeToUtc(values.scheduledStartAt, zone).toISOString(),
    durationMinutes: values.durationMinutes,
    assignedUserId: values.assignedUserId === "none" ? null : values.assignedUserId,
    transportType: values.transportType === "none" ? null : values.transportType,
    vehicleVin: values.vehicleVin?.trim() ? values.vehicleVin.trim().toUpperCase() : null,
    notes: values.notes?.trim() ? values.notes.trim() : null,
  };
}

type AppointmentFormDialogProps = {
  mode: "create" | "edit";
  appointment?: Appointment | null;
  /** Store-local wall-time string a calendar slot click pre-fills. */
  presetStart?: string | null;
  /** Contact a `?contactId=` deep link (Contact 360) pre-fills. */
  presetContactId?: string | null;
  onClose: () => void;
};

/**
 * Book or edit an appointment. Self-sufficient: it fetches its own
 * reference data and invalidates the appointment queries on save, so any
 * surface can mount it with just a mode and a close handler (the calendar
 * page, Contact 360's deep link, the shell's New menu).
 */
export function AppointmentFormDialog(props: AppointmentFormDialogProps) {
  const rooftopsQuery = useQuery({
    queryKey: ["bdc", "rooftops"],
    queryFn: bdcApi.rooftops,
  });
  // The form snapshots rooftops[0] for its rooftop and zone defaults at
  // mount, so it must not mount while the list is still resolving — a
  // fresh tab would otherwise book against an empty rooftop. Rendering
  // nothing in the meantime is not an option: the caller has already
  // committed to opening (its menu closed), so a slow or failed rooftops
  // request would read as a dead button with no way to recover.
  if (!rooftopsQuery.data) {
    return (
      <Dialog open onOpenChange={(o) => !o && props.onClose()}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Book appointment</DialogTitle>
            <DialogDescription>
              {rooftopsQuery.isError
                ? "Couldn't load your rooftops, so there is nowhere to book yet."
                : "Loading your rooftops…"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            {rooftopsQuery.isError && (
              <Button
                variant="outline"
                onClick={() => void rooftopsQuery.refetch()}
              >
                Try again
              </Button>
            )}
            <Button variant="ghost" onClick={props.onClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
  return <AppointmentForm {...props} rooftops={rooftopsQuery.data} />;
}

function AppointmentForm({
  mode,
  appointment = null,
  presetStart = null,
  presetContactId = null,
  rooftops,
  onClose,
}: AppointmentFormDialogProps & { rooftops: Rooftop[] }) {
  const qc = useQueryClient();
  const zoneFor = useRooftopZone();
  // Snapshot once at mount — defaultSlot() tracks the wall clock, so
  // recomputing it per render would read as a dirty form after an hour
  // boundary. useZodForm seeds from the same first-render value.
  const [initial] = useState(() =>
    appointment
      ? toEditValues(appointment, zoneFor(appointment.rooftopId))
      : {
          ...EMPTY,
          contactId: presetContactId ?? EMPTY.contactId,
          rooftopId: rooftops[0]?.id ?? "",
          scheduledStartAt: presetStart ?? defaultSlot(zoneFor(rooftops[0]?.id ?? null)),
        },
  );
  const form = useZodForm(AppointmentFormSchema, initial);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const usersQuery = useQuery({ queryKey: ["bdc", "users"], queryFn: bdcApi.users });
  // Per-rooftop appointment catalog. Picking a row drives the reminder
  // ladder's template; "Auto" lets the server resolve the rooftop's
  // default for the appointment type.
  const serviceTypesQuery = useQuery({
    queryKey: ["bdc", "serviceTypes"],
    queryFn: bdcApi.serviceTypes,
  });

  function invalidate() {
    return qc.invalidateQueries({ queryKey: ["bdc", "appointments"] });
  }

  const createMutation = useMutation({
    mutationFn: (input: AppointmentFormValues) =>
      bdcApi.createAppointment({
        contactId: input.contactId,
        rooftopId: input.rooftopId,
        appointmentType: input.appointmentType,
        serviceTypeId: input.serviceTypeId === "none" ? null : input.serviceTypeId,
        status: input.status,
        // The typed value is wall time at the SELECTED rooftop.
        scheduledStartAt: wallTimeToUtc(
          input.scheduledStartAt,
          zoneFor(input.rooftopId),
        ).toISOString(),
        durationMinutes: input.durationMinutes,
        assignedUserId: input.assignedUserId === "none" ? null : input.assignedUserId,
        transportType: input.transportType === "none" ? null : input.transportType,
        vehicleVin: input.vehicleVin?.trim() ? input.vehicleVin.trim().toUpperCase() : null,
        notes: input.notes?.trim() ? input.notes.trim() : null,
      }),
    onSuccess: async () => {
      await invalidate();
      toast.success("Appointment booked");
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: (input: AppointmentFormValues) =>
      bdcApi.updateAppointment(
        appointment!.id,
        toUpdatePayload(input, zoneFor(input.rooftopId)),
      ),
    onSuccess: async () => {
      await invalidate();
      toast.success("Appointment updated");
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  // Cancelling sends ONLY the status change — never the rest of the
  // form, which may hold unvalidated, half-finished edits.
  const cancelMutation = useMutation({
    mutationFn: () =>
      bdcApi.updateAppointment(appointment!.id, { status: "cancelled" }),
    onSuccess: async () => {
      await invalidate();
      toast.success("Appointment cancelled");
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function submit() {
    const result = form.validate();
    if (!result.ok) return;
    if (mode === "create") createMutation.mutate(result.data);
    else updateMutation.mutate(result.data);
  }

  const { values, errors, setField } = form;
  const isPending =
    createMutation.isPending || updateMutation.isPending || cancelMutation.isPending;
  const humanUsers = assignableUsers(usersQuery.data ?? []);
  // Options are the SELECTED rooftop's catalog; archived rows stay
  // hidden except the one an existing appointment already points at.
  const serviceTypeOptions = (serviceTypesQuery.data ?? []).filter(
    (st) =>
      st.rooftopId === values.rooftopId && (st.active || st.id === values.serviceTypeId),
  );

  // Unsaved edits block the implicit close paths (outside click, Escape);
  // the X and Close buttons still work.
  const dirty =
    values.contactId !== initial.contactId ||
    values.rooftopId !== initial.rooftopId ||
    values.appointmentType !== initial.appointmentType ||
    values.serviceTypeId !== initial.serviceTypeId ||
    values.status !== initial.status ||
    values.scheduledStartAt !== initial.scheduledStartAt ||
    values.durationMinutes !== initial.durationMinutes ||
    values.assignedUserId !== initial.assignedUserId ||
    values.transportType !== initial.transportType ||
    (values.vehicleVin ?? "") !== (initial.vehicleVin ?? "") ||
    (values.notes ?? "") !== (initial.notes ?? "");

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="sm:max-w-2xl" preventDismiss={dirty}>
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Book appointment" : "Edit appointment"}</DialogTitle>
          <DialogDescription>
            Schedule a visit. Attach a contact and rooftop; optionally an assignee and vehicle.
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[60vh] gap-4 overflow-y-auto -mx-1 px-1">
          <FieldGroup title="Who & where">
            <FormField label="Contact" required error={errors.contactId}>
              <ContactSearchPicker
                value={values.contactId}
                onSelect={(contact) => setField("contactId", contact?.id ?? "")}
                disabled={mode === "edit"}
                placeholder="Search for a contact"
              />
            </FormField>
            <FormField label="Rooftop" required error={errors.rooftopId}>
              <Select
                value={values.rooftopId}
                onValueChange={(v) => {
                  setField("rooftopId", v);
                  // Catalogs are per rooftop — a service type picked for
                  // the old rooftop would be silently wrong for the new.
                  setField("serviceTypeId", "none");
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick a rooftop" />
                </SelectTrigger>
                <SelectContent>
                  {rooftops.map((rooftop) => (
                    <SelectItem key={rooftop.id} value={rooftop.id}>
                      {rooftop.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </FieldGroup>

          <FieldGroup title="Schedule">
            <FormField label="Type" required error={errors.appointmentType}>
              <Select
                value={values.appointmentType}
                onValueChange={(v) => setField("appointmentType", v as AppointmentType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AppointmentTypeSchema.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {humanize(option)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Service type" error={errors.serviceTypeId}>
              <Select
                value={values.serviceTypeId}
                onValueChange={(v) => setField("serviceTypeId", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Auto (match type)</SelectItem>
                  {serviceTypeOptions.map((st) => (
                    <SelectItem key={st.id} value={st.id}>
                      {st.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Status" required error={errors.status}>
              <Select
                value={values.status}
                onValueChange={(v) => setField("status", v as AppointmentStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AppointmentStatusSchema.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {humanize(option)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField
              label="Date & time"
              required
              error={errors.scheduledStartAt}
              description={`Store time — ${zoneFor(values.rooftopId)}`}
            >
              <Input
                type="datetime-local"
                value={values.scheduledStartAt}
                onChange={(e) => setField("scheduledStartAt", e.target.value)}
              />
            </FormField>
            <FormField label="Duration (min)" required error={errors.durationMinutes}>
              <Input
                type="number"
                min={5}
                max={480}
                step={5}
                value={values.durationMinutes}
                onChange={(e) => setField("durationMinutes", Number(e.target.value) || 0)}
              />
            </FormField>
          </FieldGroup>

          <FieldGroup title="Assignment & logistics">
            <FormField label="Assigned to" error={errors.assignedUserId}>
              <Select
                value={values.assignedUserId}
                onValueChange={(v) => setField("assignedUserId", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {humanUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.displayName ?? user.email ?? "Unnamed"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Transport" error={errors.transportType}>
              <Select
                value={values.transportType}
                onValueChange={(v) => setField("transportType", v as TransportType | "none")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No transport</SelectItem>
                  {AppointmentTransportTypeSchema.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {humanize(option)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField className="sm:col-span-2" label="Vehicle VIN" optional error={errors.vehicleVin}>
              <Input
                value={values.vehicleVin ?? ""}
                onChange={(e) => setField("vehicleVin", e.target.value)}
                placeholder="If a specific vehicle is involved"
                className="font-mono"
              />
            </FormField>
            <FormField className="sm:col-span-2" label="Notes" optional error={errors.notes}>
              <Textarea
                value={values.notes ?? ""}
                onChange={(e) => setField("notes", e.target.value)}
                rows={3}
                placeholder="Anything operators or the customer should know."
              />
            </FormField>
          </FieldGroup>
        </div>

          <DialogFooter>
          {mode === "edit" && values.status !== "cancelled" && (
            <Button
              variant="destructive"
              onClick={() => setCancelConfirmOpen(true)}
              disabled={isPending}
            >
              Cancel appointment
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending
              ? "Saving…"
              : mode === "create"
                ? "Book appointment"
                : "Save changes"}
          </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={cancelConfirmOpen}
        onOpenChange={setCancelConfirmOpen}
        title="Cancel this appointment?"
        description="The appointment remains in the history with a cancelled status. Other unsaved edits in this form will not be applied."
        confirmLabel="Cancel appointment"
        destructive
        pending={cancelMutation.isPending}
        onConfirm={() => cancelMutation.mutate()}
      />
    </>
  );
}

function FieldGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="grid gap-3">
      <legend className="text-sm font-semibold text-muted-foreground">
        {title}
      </legend>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}
